import React from "react";
import { Minus, Plus, X, Trash2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useProducts } from "@/hooks/useProducts";
import { optimizedImg } from "@/lib/img";
import { motion, AnimatePresence } from "framer-motion";
import DiscountCodeInput from "@/components/DiscountCodeInput";

const APPAREL_CATS = new Set(["hoodies", "sweatshirts", "tees", "cropped-tees"]);
const ACCESSORY_IDS_FOR_BUNDLE = ["bottle-maroon", "notebook-yellow", "notebook-maroon"];

/**
 * Pick the two best upsells for the current cart:
 *  - Always include `bottle-maroon` (the show's bottle, every cart should want one)
 *  - Pair with the matching-colour notebook (maroon if cart has any
 *    maroon/burgundy SKU, otherwise the yellow notebook)
 *  - Hide anything already in the cart.
 */
function pickUpsells(items, products) {
  if (!items.length || !products.length) return [];
  const inCartIds = new Set(items.map((i) => i.id));
  const hasApparel = items.some((i) => {
    const p = products.find((x) => x.id === i.id);
    return p && APPAREL_CATS.has(p.category);
  });
  if (!hasApparel) return [];
  const hasMaroon = items.some((i) => /maroon|burgundy/i.test(i.id));
  const preferred = [
    "bottle-maroon",
    hasMaroon ? "notebook-maroon" : "notebook-yellow",
  ];
  const candidates = preferred
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean)
    .filter((p) => !inCartIds.has(p.id));
  // If we filtered too aggressively, fall back to any accessory not yet in cart.
  if (candidates.length === 0) {
    return products
      .filter((p) => ACCESSORY_IDS_FOR_BUNDLE.includes(p.id) && !inCartIds.has(p.id))
      .slice(0, 2);
  }
  return candidates.slice(0, 2);
}

export default function CartDrawer() {
  const { items, open, setOpen, inc, dec, remove, total, add, discount } = useCart();
  const { products } = useProducts();
  const navigate = useNavigate();
  const upsells = pickUpsells(items, products);

  const goToCheckout = () => {
    if (!items.length) return;
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/50"
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 240 }}
            className="fixed top-0 right-0 z-50 h-full w-full sm:w-[440px] bg-[var(--st-bg)] border-l-2 border-[#111] shadow-brutal flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[#111] bg-[var(--st-maroon)] text-white">
              <p className="font-display uppercase tracking-tight text-2xl">Your Bag</p>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close cart"
                data-testid="close-cart-button"
                className="border-2 border-white p-1.5 hover:bg-white hover:text-[var(--st-maroon)] transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-3">
                  <p className="font-display uppercase text-3xl text-[var(--st-fg)]/40">Empty</p>
                  <p className="font-body text-sm text-[var(--st-fg)]/60 max-w-[260px]">
                    Class hasn&apos;t started yet. Pick something off the syllabus.
                  </p>
                  <button
                    onClick={() => setOpen(false)}
                    className="btn-brutal mt-2"
                    data-testid="cart-shop-now"
                  >
                    Shop Now
                  </button>
                </div>
              ) : (
                <ul className="flex flex-col divide-y-2 divide-[#11111122]">
                  {items.map((i) => (
                    <li
                      key={i.key}
                      data-testid={`cart-item-${i.key}`}
                      className="cart-item-row flex gap-3 py-3 items-start"
                    >
                      <div className="w-16 h-20 border-2 border-[#111] overflow-hidden shrink-0">
                        <img
                          src={optimizedImg(i.image, 160)}
                          alt={i.name}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display uppercase tracking-tight text-base leading-tight truncate">
                          {i.name}
                        </p>
                        <p className="font-heading uppercase tracking-[0.18em] text-[10px] text-[var(--st-fg)]/60 mt-0.5">
                          <span className="font-price text-base text-[var(--st-fg)]/80 tracking-tight align-middle">
                            ${i.price.toFixed(2)}
                          </span>{" "}
                          each
                          {i.size && (
                            <>
                              {" · "}
                              <span data-testid={`cart-size-${i.key}`} className="text-[var(--st-maroon)]">
                                Size {i.size}
                              </span>
                            </>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="inline-flex items-center border-2 border-[#111]">
                            <button
                              onClick={() => dec(i.key)}
                              data-testid={`dec-${i.key}`}
                              className="px-2 py-1 hover:bg-[var(--st-orange)]"
                            >
                              <Minus size={12} />
                            </button>
                            <span className="px-3 font-display text-sm">{i.quantity}</span>
                            <button
                              onClick={() => inc(i.key)}
                              data-testid={`inc-${i.key}`}
                              className="px-2 py-1 hover:bg-[var(--st-orange)]"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                          <button
                            onClick={() => remove(i.key)}
                            data-testid={`remove-${i.key}`}
                            className="ml-auto inline-flex items-center gap-1 text-[var(--st-maroon)] text-xs hover:underline"
                          >
                            <Trash2 size={12} /> Remove
                          </button>
                        </div>
                      </div>
                      <p className="font-price text-xl text-[var(--st-maroon)] leading-none whitespace-nowrap">
                        ${(i.price * i.quantity).toFixed(2)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* CART UPSELL — accessories that pair with apparel in cart */}
            {upsells.length > 0 && (
              <div
                data-testid="cart-upsell"
                className="border-t-2 border-[#111] px-5 py-3 bg-[var(--st-bg)]"
              >
                <div className="flex items-center justify-between mb-2.5">
                  <p className="font-display uppercase tracking-tight text-base text-[var(--st-maroon)]">
                    Class supplies you&apos;ll want
                  </p>
                  <span className="font-heading uppercase tracking-[0.18em] text-[10px] text-[var(--st-fg)]/55">
                    +1 click
                  </span>
                </div>
                <ul className="flex flex-col gap-2">
                  {upsells.map((u) => (
                    <li
                      key={u.id}
                      data-testid={`upsell-${u.id}`}
                      className="flex items-center gap-3 border-2 border-[#111] bg-[var(--st-cream)] p-2"
                    >
                      <div className="w-12 h-12 border-2 border-[#111] overflow-hidden shrink-0 bg-white">
                        <img
                          src={optimizedImg(u.image, 120)}
                          alt={u.name}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display uppercase tracking-tight text-sm leading-tight truncate">
                          {u.name}
                        </p>
                        <p className="font-price text-base text-[var(--st-maroon)] leading-none mt-0.5">
                          ${u.price}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => add(u, null)}
                        data-testid={`upsell-add-${u.id}`}
                        className="inline-flex items-center gap-1 bg-[var(--st-mustard)] text-[var(--st-fg)] border-2 border-[#111] px-2.5 py-1.5 font-heading uppercase tracking-[0.18em] text-[10px] hover:bg-[var(--st-orange)] transition-colors shadow-brutal-sm"
                      >
                        <Plus size={12} /> Add
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <DiscountCodeInput variant="drawer" />

            <div className="border-t-2 border-[#111] px-5 py-4 bg-[var(--st-cream)]">
              {discount ? (
                <>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-heading uppercase tracking-[0.2em] text-[11px] text-[var(--st-fg)]/70">
                      Items subtotal
                    </span>
                    <span
                      data-testid="cart-subtotal"
                      className="font-price text-lg text-[var(--st-fg)]/70 line-through"
                    >
                      ${total.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="font-heading uppercase tracking-[0.2em] text-[11px] text-[var(--st-maroon)]">
                      {discount.code} · −{discount.percent_off}%
                    </span>
                    <span
                      data-testid="cart-discount-amount"
                      className="font-price text-lg text-[var(--st-maroon)]"
                    >
                      −${discount.discount_amount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t-2 border-[#111] pt-2">
                    <span className="font-heading uppercase tracking-[0.2em] text-sm">After discount</span>
                    <span
                      data-testid="cart-subtotal-after"
                      className="font-price text-3xl text-[var(--st-maroon)]"
                    >
                      ${discount.subtotal_after.toFixed(2)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between mb-3">
                  <span className="font-heading uppercase tracking-[0.2em] text-sm">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-price text-3xl text-[var(--st-maroon)]">
                    ${total.toFixed(2)}
                  </span>
                </div>
              )}
              <p className="font-body text-xs text-[var(--st-fg)]/60 mt-3 mb-3">
                {discount?.free_shipping
                  ? "Free shipping applied. Taxes calculated at checkout."
                  : "Shipping & taxes calculated at checkout. Powered by Stripe."}
              </p>
              <button
                onClick={goToCheckout}
                disabled={!items.length}
                data-testid="checkout-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[var(--st-maroon)] text-white border-2 border-[#111] px-5 py-3 font-heading uppercase tracking-[0.18em] text-sm hover:bg-[var(--st-mustard)] hover:text-[var(--st-fg)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-brutal-sm"
              >
                Continue to Checkout <ArrowRight size={14} />
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
